import type { IconType } from '@/lib/icons'
import { ChevronLeft, ChevronRight } from '@/lib/icons'
import { cn } from '@/lib/utils'

interface PaginationProps {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
}

/** `docs/03-app-flow.md` Sahifa 3 namunasi: "[1] 2 3 ... 25 →". */
export function Pagination({ page, pageSize, total, onPageChange }: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  if (pageCount <= 1) return null

  const pages = buildPageList(page, pageCount)

  return (
    <nav aria-label="Sahifalash" className="flex items-center justify-center gap-1 pt-2">
      <PageButton
        label="Oldingi"
        icon={ChevronLeft}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      />
      {pages.map((entry, index) =>
        entry === 'ellipsis' ? (
          <span key={`ellipsis-${index}`} className="px-1 text-sm text-app-hint">
            …
          </span>
        ) : (
          <button
            key={entry}
            type="button"
            onClick={() => onPageChange(entry)}
            aria-current={entry === page ? 'page' : undefined}
            className={cn(
              'flex h-11 min-w-11 items-center justify-center rounded-button px-2 text-sm font-medium tabular transition-colors duration-150 ease-out',
              entry === page ? 'bg-brand text-brand-fg' : 'text-app-text hover:bg-app-card',
            )}
          >
            {entry}
          </button>
        ),
      )}
      <PageButton
        label="Keyingi"
        icon={ChevronRight}
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
      />
    </nav>
  )
}

function PageButton({
  label,
  icon: Icon,
  disabled,
  onClick,
}: {
  label: string
  icon: IconType
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="flex size-11 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-app-card disabled:pointer-events-none disabled:opacity-40"
    >
      <Icon size={16} aria-hidden="true" />
    </button>
  )
}

type PageEntry = number | 'ellipsis'

/** 1, tegishlilar joriy sahifa atrofida, oxirgisi — orasidagi bo'shliqlar "…" bilan. */
function buildPageList(current: number, pageCount: number): PageEntry[] {
  const delta = 1
  const range: number[] = []
  for (let i = Math.max(2, current - delta); i <= Math.min(pageCount - 1, current + delta); i++) {
    range.push(i)
  }

  const result: PageEntry[] = [1]
  if (range.length > 0 && range[0]! > 2) {
    result.push('ellipsis')
  } else if (range.length === 0 && pageCount > 2 && current > 2) {
    result.push('ellipsis')
  }
  result.push(...range)
  if (range.length > 0 && range[range.length - 1]! < pageCount - 1) {
    result.push('ellipsis')
  } else if (range.length === 0 && pageCount > 2 && current < pageCount - 1) {
    result.push('ellipsis')
  }
  if (pageCount > 1) result.push(pageCount)

  return result
}
